/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';
import { useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  Accordion,
  AccordionSection,
  Headline,
  Layout,
  LayoutItem,
  Text,
} from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../i18n/messages';
import type { DepartmentOpeningHours, PbcPartner } from './PartnerData';
import { useMedia } from '../../lib/hooks';
import { OpeningHoursDataList } from './OpeningHoursDataList';
import { OpeningHoursLabel } from './OpeningHoursLabel';
import { camelize, canUseDOM, throttle } from '../../lib/utils';
import { useTrackingManager } from '../useTrackingManager';

// SC

// @todo: fix sizing as soon as text is translated
const DepartmentsGrid = styled.div`
  display: flex;
  max-width: 100%;
  flex-wrap: wrap;
`;

const DepartmentsGridItem = styled.div`
  box-sizing: border-box;
  width: 318px;
  padding-bottom: 3rem;
  margin-right: 2rem;
`;

const OpeningHoursNoteContainer = styled(LayoutItem)`
  @media (min-width: 768px) {
    border-top: 1px solid #808080;
  }
`;

const OpeningHoursMainContainer = styled(LayoutItem)`
  > span {
    display: block;
  }
  @media (min-width: 768px) {
    max-width: 100%;
  }
`;

const StyledAccordion = styled(Accordion)`
  > div {
    background: rgba(0, 0, 0, 0.05);
  }
`;

// Components

interface OpeningHoursProps {
  data: PbcPartner;
  hasHeadline?: boolean;
  isNarrow?: boolean;
}

export const OpeningHours: React.FunctionComponent<OpeningHoursProps> = ({
  data,
  hasHeadline = true,
  isNarrow = false,
}) => {
  const [openState, setOpenState] = React.useState<boolean[]>([]);
  const departmentCount = data?.openingHours?.departments?.length || 0;

  useEffect(() => {
    const blankArray = new Array(departmentCount);
    const newOpenState = blankArray.map(() => false);
    if (newOpenState.length === 1) {
      newOpenState[0] = true;
    }
    setOpenState(newOpenState);
  }, [departmentCount]);

  const isViewportM = useMedia(`only screen and (min-width: 768px)`);

  const gridElement = useRef<HTMLDivElement>(null);
  const renderAsAccordion = !isViewportM || isNarrow;
  const openingHoursHeadline = useI18n(messages.openingHoursHeadline);

  useEffect(() => {
    const $gridContainer = gridElement.current;
    const $gridCellHeaders = $gridContainer?.querySelectorAll<HTMLDivElement>(
      '[data-testid^="pbc-opening-hours-grid-header-"]',
    );

    const columnConform = (elements: NodeListOf<HTMLDivElement> | undefined): void => {
      if (!elements) {
        return;
      }

      const elementCollection: Record<string, { height: number; elements: HTMLDivElement[] }> =
        Array.from(elements).reduce<Record<string, { height: number; elements: HTMLDivElement[] }>>(
          (acc, curr) => {
            curr.style.height = 'auto';
            acc[`${curr.offsetTop}`] = acc[`${curr.offsetTop}`] || {
              height: 0,
              elements: [],
            };
            acc[`${curr.offsetTop}`].height =
              curr.clientHeight > acc[`${curr.offsetTop}`].height
                ? curr.clientHeight
                : acc[`${curr.offsetTop}`].height;
            acc[`${curr.offsetTop}`].elements.push(curr);
            return acc;
          },
          {},
        );

      Object.keys(elementCollection).forEach((key) => {
        elementCollection[key].elements.forEach((el) => {
          el.style.height = `${elementCollection[key].height}px`;
        });
      });
    };
    columnConform($gridCellHeaders);
    const throttledColumnConform = throttle(() => {
      columnConform($gridCellHeaders);
    }, 200);
    if (canUseDOM) {
      window.addEventListener('resize', throttledColumnConform);
    }
    return () => {
      if (canUseDOM) {
        window.removeEventListener('resize', throttledColumnConform);
      }
    };
  }, []);

  const now = new Date();
  const trackingManager = useTrackingManager();
  let departments: DepartmentOpeningHours[] = [];
  let openingHoursNote;

  if (data.openingHours) {
    departments = data.openingHours.departments || [];
    openingHoursNote = data.openingHours.openingHoursNote;
  }

  useEffect(() => {
    // Remove aria-hidden="true" from div[role="region"] because the UI library injects it
    // and it causes accessibility errors (WCAG/axe). This is a temporary workaround until the library fixes it.
    setTimeout(() => {
      document.querySelectorAll('div[role="region"][aria-hidden="true"]').forEach((el) => {
        el.removeAttribute('aria-hidden');
      });
    }, 100);
  }, []);

  return (
    <OpeningHoursMainContainer data-testid="pbc-opening-hours">
      {hasHeadline && (
        <Headline
          tag="span"
          variant={{ type: 'headline', order: '3', weight: 'normal' }}
          margin={[0, 0, 'var(--spacing-relative-xl)', 0]}
          data-testid="pbc-opening-hours-heading"
        >
          {openingHoursHeadline}
        </Headline>
      )}
      {renderAsAccordion && (
        <StyledAccordion>
          {departments.map((department: DepartmentOpeningHours, i: number) => (
            <AccordionSection
              label={(<OpeningHoursLabel department={department} now={now} isNarrow />) as any}
              headingTag="h3"
              data-testid={`pbc-opening-hours-department-${camelize(department.departmentName)}`}
              key={i}
              open={openState[i]}
              onToggle={() => {
                const newOpenState = openState.slice();
                newOpenState[i] = !openState[i];
                setOpenState(newOpenState);
                trackingManager.click(
                  'content',
                  '',
                  'accordion',
                  department.departmentName,
                  data.nameEnhanced,
                  (i + 1).toString(),
                  department.departmentName,
                );
              }}
            >
              <Text margin={[0, 0, 'var(--spacing-relative-sm)', 0]}>
                {department.departmentOpeningHoursNote}
              </Text>
              <OpeningHoursDataList department={department} now={now} />
            </AccordionSection>
          ))}
        </StyledAccordion>
      )}
      {isViewportM && !isNarrow && (
        <DepartmentsGrid ref={gridElement}>
          {departments?.map((department: DepartmentOpeningHours, i: number) => (
            <DepartmentsGridItem key={i}>
              <Layout direction="column">
                <LayoutItem
                  data-testid={`pbc-opening-hours-grid-header-${camelize(department.departmentName)}`}
                >
                  <OpeningHoursLabel department={department} now={now} />
                  <Text margin={['var(--spacing-relative-sm)', 0, 'var(--spacing-relative-lg)', 0]}>
                    {department.departmentOpeningHoursNote}
                  </Text>
                </LayoutItem>
                <LayoutItem
                  data-testid={`pbc-opening-hours-grid-${camelize(department.departmentName)}`}
                >
                  <OpeningHoursDataList department={department} now={now} />
                </LayoutItem>
              </Layout>
            </DepartmentsGridItem>
          ))}
        </DepartmentsGrid>
      )}
      {openingHoursNote && (
        <OpeningHoursNoteContainer>
          <Text
            data-testid="pbc-opening-hours-note"
            variant={{ type: 'copy', order: '2', style: 'normal' }}
            margin={['var(--spacing-relative-md)', 0, 0, 0]}
          >
            {openingHoursNote}
          </Text>
        </OpeningHoursNoteContainer>
      )}
    </OpeningHoursMainContainer>
  );
};
